import React, { useEffect, useRef, useState } from 'react';
import appointmentService from '../../services/appointmentService';
import registrationService from '../../services/registrationService';
import SlotPicker, { describeSlotError } from './SlotPicker';
import TestPicker from './TestPicker';
import { buildCreatePayload, formatDateLabel, isHomeVisit, newIdempotencyKey, pickQuickSlot, todayIso, validateBooking } from '../../utils/appointmentLogic';

const emptyForm = (centreId, date) => ({
  tests: [],
  collectionAddress: '',
  collectionPincode: '',
  phlebotomistName: '',
  rebookedFromId: null,
  repeat: false,
  repeatMode: 'WEEKLY',
  occurrences: 4,
  patientId: '',
  patientLabel: '',
  title: 'Mr.',
  pName: '',
  gender: '',
  dob: '',
  ageYear: '',
  mobile: '',
  email: '',
  centreId: centreId || '',
  departmentId: '',
  visitType: 'Center Visit',
  doctorId: '',
  purpose: '',
  remarks: '',
  appointmentDate: date || todayIso(),
  slotStart: '',
});

/**
 * prefill: { form, patientInfo } from "Rebook", or { form: { appointmentDate, slotStart } } when a free slot was picked in the day view.
 * onBookedSeries is called instead of onBooked when the booking repeats.
 */
export const AppointmentBookingModal = ({ isOpen, onClose, onBooked, onBookedSeries, lookups, departments, defaultCentreId, defaultDate, isAdmin, prefill }) => {
  const [form, setForm] = useState(emptyForm(defaultCentreId, defaultDate));
  const [mode, setMode] = useState('new'); // 'new' | 'existing'
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [duplicateNote, setDuplicateNote] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [slotRefresh, setSlotRefresh] = useState(0);
  const [patientInfo, setPatientInfo] = useState(null);
  const [mobileMatches, setMobileMatches] = useState([]);
  const [quickBusy, setQuickBusy] = useState(false);
  const [quickMsg, setQuickMsg] = useState('');
  const submittingRef = useRef(false);
  const keyRef = useRef(newIdempotencyKey());

  // A fresh form and a fresh idempotency key every time the modal opens.
  useEffect(() => {
    if (isOpen) {
      const merged = { ...emptyForm(defaultCentreId, defaultDate), ...(prefill?.form || {}) };
      setForm(merged);
      setMode(merged.patientId ? 'existing' : 'new');
      setQuery('');
      setResults([]);
      setDuplicateNote('');
      setError('');
      setPatientInfo(prefill?.patientInfo || null);
      setMobileMatches([]);
      setQuickMsg('');
      setSubmitting(false);
      submittingRef.current = false;
      keyRef.current = newIdempotencyKey();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, defaultCentreId, defaultDate, prefill]);

  // Debounced search of existing patients (the same endpoint Patient Registration uses).
  useEffect(() => {
    if (mode !== 'existing' || query.trim().length < 2) {
      setResults([]);
      return undefined;
    }
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await registrationService.searchPatients(query.trim(), 8);
        setResults(res.success ? res.data || [] : []);
      } catch (err) {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [query, mode]);

  // Esc closes the form (unless a booking is being saved).
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape' && !submittingRef.current) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const pickPatient = (p) => {
    set({ patientId: p.patientId, patientLabel: `${p.fullName || p.pName} · ${p.patientId} · ${p.mobile}` });
    setPatientInfo(p);
    setResults([]);
    setQuery('');
  };

  // "This patient is coming in about an hour" / "next free slot": picks today's first bookable slot from the server list.
  const quickPick = async (offsetMinutes) => {
    setQuickBusy(true);
    setQuickMsg('');
    try {
      const today = todayIso();
      const res = await appointmentService.slots(today, form.centreId);
      const slot = res.success && res.data && !res.data.isClosed ? pickQuickSlot(res.data.slots, new Date(), offsetMinutes) : null;
      if (slot) set({ appointmentDate: today, slotStart: slot });
      else setQuickMsg(res.data?.closedReason || 'No free slot is left today for that time. Choose another day below.');
    } catch (err) {
      setQuickMsg(describeSlotError(err));
    } finally {
      setQuickBusy(false);
    }
  };

  // Typing a number into the new-patient form: if it already belongs to a registered patient, offer that patient
  // (so the same person is not entered twice). Same search the existing-patient tab uses.
  const lookupByMobile = async () => {
    const digits = (form.mobile || '').replace(/\D/g, '');
    setMobileMatches([]);
    if (form.patientId || digits.length < 8) return;
    try {
      const res = await registrationService.searchPatients(digits, 5);
      const found = (res.success ? res.data || [] : []).filter((p) => (p.mobile || '').replace(/\D/g, '').includes(digits));
      setMobileMatches(found);
    } catch (err) {
      /* the suggestion is optional */
    }
  };

  const selectMatchedPatient = (p) => {
    setMode('existing');
    pickPatient(p);
    setMobileMatches([]);
    setDuplicateNote('');
  };

  const checkDuplicate = async () => {
    setDuplicateNote('');
    if (form.patientId || !form.pName.trim() || !form.mobile.trim()) return;
    try {
      const res = await registrationService.checkDuplicate(form.mobile.trim(), form.pName.trim());
      if (res.success && res.data?.isDuplicate) {
        setDuplicateNote('A patient with this name and mobile already exists. Use "Existing patient" and select them to avoid a duplicate record.');
      }
    } catch (err) {
      /* the hint is optional */
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (submittingRef.current) return;
    const problem = validateBooking(form);
    if (problem) {
      setError(problem);
      return;
    }
    submittingRef.current = true;
    setSubmitting(true);
    setError('');
    try {
      const payload = buildCreatePayload(form, keyRef.current);
      if (form.repeat) {
        const res = await appointmentService.createRecurring({ ...payload, repeat: form.repeatMode, occurrences: parseInt(form.occurrences, 10) });
        if (res.success) onBookedSeries(res.data, res.message);
        else setError(res.message || 'The appointments could not be booked.');
        return;
      }
      const res = await appointmentService.create(payload);
      if (res.success) {
        onBooked(res.data);
      } else {
        setError(res.message || 'The appointment could not be booked.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'The appointment could not be booked.');
      // The slot may have just been taken; reload the grid and make the user choose again.
      setSlotRefresh((n) => n + 1);
      set({ slotStart: '' });
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  const titles = lookups?.titles || [];
  const genders = lookups?.genders || [];
  const visitTypes = lookups?.visitTypes || [];
  const doctors = lookups?.doctors || [];
  const centres = lookups?.centres || [];

  return (
    <div className="apt-backdrop" onClick={() => !submitting && onClose()}>
      <form className="apt-modal apt-modal-wide" onClick={(e) => e.stopPropagation()} onSubmit={submit} noValidate>
        <div className="apt-modal-head">
          <div>
            <h2>{form.rebookedFromId ? 'Rebook appointment' : 'New appointment'}</h2>
            <p className="apt-modal-sub">Reserve a time slot. Billing is done at registration; tests chosen here are only carried over.</p>
          </div>
          <button type="button" className="apt-icon-btn" onClick={onClose} disabled={submitting} aria-label="Close">×</button>
        </div>

        <div className="apt-modal-body">
          <div className="apt-section-title"><span className="apt-step">1</span> Patient</div>
          <div className="apt-seg" role="tablist" aria-label="Patient type">
            <button type="button" role="tab" aria-selected={mode === 'new'} className={mode === 'new' ? 'active' : ''}
              onClick={() => { setMode('new'); set({ patientId: '', patientLabel: '' }); }}>New patient</button>
            <button type="button" role="tab" aria-selected={mode === 'existing'} className={mode === 'existing' ? 'active' : ''}
              onClick={() => setMode('existing')}>Existing patient</button>
          </div>

          {mode === 'existing' ? (
            <div className="apt-section">
              {form.patientId ? (
                <div className="apt-patient-card">
                  <span className="apt-avatar apt-avatar-lg" aria-hidden="true">{(patientInfo?.pName || form.patientLabel || '?').split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase()}</span>
                  <div className="apt-patient-card-body">
                    <strong>{patientInfo?.fullName || patientInfo?.pName || form.patientLabel}</strong>
                    <div className="apt-muted">
                      {patientInfo ? [patientInfo.gender, patientInfo.ageYear !== null && patientInfo.ageYear !== undefined ? `${patientInfo.ageYear}Y` : null, patientInfo.mobile].filter(Boolean).join(' · ') : ''}
                    </div>
                    <span className="apt-uhid">{form.patientId}</span>
                    <span className="apt-hint"> Registered patient. Their details fill in automatically at registration.</span>
                  </div>
                  <button type="button" className="apt-link" onClick={() => { set({ patientId: '', patientLabel: '' }); setPatientInfo(null); }}>Change</button>
                </div>
              ) : (
                <>
                  <label className="apt-label" htmlFor="apt-search">Search by name, mobile or UHID</label>
                  <input id="apt-search" className="apt-input" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="At least 2 characters" autoFocus />
                  {searching && <p className="apt-hint">Searching…</p>}
                  {results.length > 0 && (
                    <ul className="apt-results">
                      {results.map((p) => (
                        <li key={p.patientId}>
                          <button type="button" onClick={() => pickPatient(p)}>
                            <strong>{p.fullName || p.pName}</strong>
                            <span>{p.patientId} · {p.mobile} · {p.gender}{p.ageYear !== null && p.ageYear !== undefined ? ` · ${p.ageYear}Y` : ''}</span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                  {query.trim().length >= 2 && !searching && results.length === 0 && <p className="apt-hint">No patient found. Use "New patient" instead.</p>}
                </>
              )}
            </div>
          ) : (
            <div className="apt-grid">
              <div className="apt-field apt-span-1">
                <label className="apt-label" htmlFor="apt-title">Title</label>
                <select id="apt-title" className="apt-input" value={form.title} onChange={(e) => set({ title: e.target.value })}>
                  {titles.length === 0 && <option value="Mr.">Mr.</option>}
                  {titles.map((t) => <option key={t.code} value={t.value}>{t.value}</option>)}
                </select>
              </div>
              <div className="apt-field apt-span-3">
                <label className="apt-label" htmlFor="apt-name">Patient name *</label>
                <input id="apt-name" className="apt-input" value={form.pName} onChange={(e) => set({ pName: e.target.value })} onBlur={checkDuplicate} maxLength={200} />
              </div>
              <div className="apt-field apt-span-2">
                <label className="apt-label" htmlFor="apt-gender">Gender *</label>
                <select id="apt-gender" className="apt-input" value={form.gender} onChange={(e) => set({ gender: e.target.value })}>
                  <option value="">Select</option>
                  {genders.map((g) => <option key={g.code} value={g.value}>{g.value}</option>)}
                </select>
              </div>
              <div className="apt-field apt-span-2">
                <label className="apt-label" htmlFor="apt-dob">Date of birth</label>
                <input id="apt-dob" type="date" className="apt-input" max={todayIso()} value={form.dob} onChange={(e) => set({ dob: e.target.value, ageYear: e.target.value ? '' : form.ageYear })} />
              </div>
              <div className="apt-field apt-span-2">
                <label className="apt-label" htmlFor="apt-age">or Age (years)</label>
                <input id="apt-age" type="number" min="0" max="120" className="apt-input" value={form.ageYear} onChange={(e) => set({ ageYear: e.target.value, dob: e.target.value !== '' ? '' : form.dob })} />
              </div>
              <div className="apt-field apt-span-3">
                <label className="apt-label" htmlFor="apt-mobile">Mobile *</label>
                <input id="apt-mobile" className="apt-input" value={form.mobile} onChange={(e) => { set({ mobile: e.target.value }); setMobileMatches([]); }} onBlur={() => { lookupByMobile(); checkDuplicate(); }} maxLength={20} inputMode="tel" />
              </div>
              <div className="apt-field apt-span-3">
                <label className="apt-label" htmlFor="apt-email">Email</label>
                <input id="apt-email" type="email" className="apt-input" value={form.email} onChange={(e) => set({ email: e.target.value })} maxLength={100} />
              </div>
              {mobileMatches.length > 0 && (
                <div className="apt-suggest" role="status">
                  <p>This mobile number belongs to a registered patient. Use their record instead of entering a new one:</p>
                  {mobileMatches.map((p) => (
                    <div key={p.patientId} className="apt-suggest-item">
                      <span>
                        <strong>{p.fullName || p.pName}</strong>
                        <span className="apt-muted"> · {p.patientId} · {p.gender}{p.ageYear !== null && p.ageYear !== undefined ? ` · ${p.ageYear}Y` : ''}</span>
                      </span>
                      <button type="button" className="apt-btn apt-btn-sm apt-btn-primary" onClick={() => selectMatchedPatient(p)}>Use this patient</button>
                    </div>
                  ))}
                </div>
              )}
              {duplicateNote && mobileMatches.length === 0 && <p className="apt-warning apt-span-6">{duplicateNote}</p>}
            </div>
          )}

          <div className="apt-section-title"><span className="apt-step">2</span> Visit details</div>
          <div className="apt-grid">
            {isAdmin && centres.length > 0 && (
              <div className="apt-field apt-span-2">
                <label className="apt-label" htmlFor="apt-centre">Centre</label>
                <select id="apt-centre" className="apt-input" value={form.centreId} onChange={(e) => set({ centreId: e.target.value, slotStart: '' })}>
                  {centres.map((c) => <option key={c.centreId} value={c.centreId}>{c.centreName}</option>)}
                </select>
              </div>
            )}
            <div className="apt-field apt-span-2">
              <label className="apt-label" htmlFor="apt-dept">Department</label>
              <select id="apt-dept" className="apt-input" value={form.departmentId} onChange={(e) => set({ departmentId: e.target.value })}>
                <option value="">Not specified</option>
                {(departments || []).map((d) => <option key={d.departmentId} value={d.departmentId}>{d.name}</option>)}
              </select>
            </div>
            <div className="apt-field apt-span-2">
              <label className="apt-label" htmlFor="apt-visit">Visit type</label>
              <select id="apt-visit" className="apt-input" value={form.visitType} onChange={(e) => set({ visitType: e.target.value })}>
                {visitTypes.length === 0 && <option value="Center Visit">Center Visit</option>}
                {visitTypes.map((v) => <option key={v.code} value={v.value}>{v.value}</option>)}
              </select>
            </div>
            <div className="apt-field apt-span-3">
              <label className="apt-label" htmlFor="apt-doctor">Referring doctor</label>
              <select id="apt-doctor" className="apt-input" value={form.doctorId} onChange={(e) => set({ doctorId: e.target.value })}>
                <option value="">Self / not specified</option>
                {doctors.map((d) => <option key={d.doctorId} value={d.doctorId}>{d.doctorName}</option>)}
              </select>
            </div>
            <div className="apt-field apt-span-3">
              <label className="apt-label" htmlFor="apt-purpose">Purpose</label>
              <input id="apt-purpose" className="apt-input" value={form.purpose} onChange={(e) => set({ purpose: e.target.value })} maxLength={300} placeholder="For example: fasting blood sugar, MRI brain" />
            </div>
            {isHomeVisit(form.visitType) && (
              <>
                <div className="apt-field apt-span-4">
                  <label className="apt-label" htmlFor="apt-address">Collection address *</label>
                  <textarea id="apt-address" className="apt-input" rows={2} value={form.collectionAddress} onChange={(e) => set({ collectionAddress: e.target.value })} maxLength={300} />
                </div>
                <div className="apt-field apt-span-2">
                  <label className="apt-label" htmlFor="apt-pin">Pincode</label>
                  <input id="apt-pin" className="apt-input" value={form.collectionPincode} onChange={(e) => set({ collectionPincode: e.target.value })} maxLength={10} inputMode="numeric" />
                </div>
                <div className="apt-field apt-span-3">
                  <label className="apt-label" htmlFor="apt-phleb">Phlebotomist (who will go)</label>
                  <input id="apt-phleb" className="apt-input" value={form.phlebotomistName} onChange={(e) => set({ phlebotomistName: e.target.value })} maxLength={100} />
                </div>
              </>
            )}
            <div className="apt-field apt-span-6">
              <TestPicker tests={form.tests} onChange={(tests) => set({ tests })} />
            </div>
          </div>

          <div className="apt-section-title"><span className="apt-step">3</span> Date and time</div>
          <div className="apt-quick" role="group" aria-label="Quick time">
            <span className="apt-hint">Quick pick for today:</span>
            <button type="button" className="apt-btn apt-btn-sm" disabled={quickBusy} onClick={() => quickPick(60)}>In about 1 hour</button>
            <button type="button" className="apt-btn apt-btn-sm" disabled={quickBusy} onClick={() => quickPick(0)}>Next available</button>
            {quickMsg && <span className="apt-error-text" style={{ margin: 0 }}>{quickMsg}</span>}
          </div>
          <div className="apt-datetime">
            <div className="apt-field apt-date-field">
              <label className="apt-label" htmlFor="apt-date">Appointment date *</label>
              <input id="apt-date" type="date" className="apt-input" min={todayIso()} value={form.appointmentDate} onChange={(e) => set({ appointmentDate: e.target.value, slotStart: '' })} />
              {form.appointmentDate && <span className="apt-hint">{formatDateLabel(form.appointmentDate)}</span>}
            </div>
            <div className="apt-slot-area">
              <span className="apt-label">Time slot *</span>
              <SlotPicker centreId={form.centreId} departmentId={form.departmentId} date={form.appointmentDate} value={form.slotStart} onChange={(s) => set({ slotStart: s })} refreshKey={slotRefresh} />
            </div>
          </div>
          {form.slotStart && (
            <div className="apt-selection" role="status">
              <strong>Selected:</strong> {formatDateLabel(form.appointmentDate)} at {form.slotStart}
            </div>
          )}

          <div className="apt-repeat">
            <label className="apt-check">
              <input type="checkbox" checked={!!form.repeat} onChange={(e) => set({ repeat: e.target.checked })} /> Repeat this appointment
            </label>
            {form.repeat && (
              <span className="apt-repeat-fields">
                <select className="apt-input apt-input-sm" aria-label="Repeat every" value={form.repeatMode} onChange={(e) => set({ repeatMode: e.target.value })}>
                  <option value="WEEKLY">every week</option>
                  <option value="DAILY">every day</option>
                </select>
                <label className="apt-inline-label">for
                  <input type="number" min="2" max="12" className="apt-input apt-input-sm" aria-label="Number of appointments" value={form.occurrences} onChange={(e) => set({ occurrences: e.target.value })} style={{ width: 64 }} />
                  appointments in total
                </label>
                <span className="apt-hint">Dates that are closed or full are skipped and listed afterwards.</span>
              </span>
            )}
          </div>

          <div className="apt-field">
            <label className="apt-label" htmlFor="apt-remarks">Remarks</label>
            <textarea id="apt-remarks" className="apt-input" rows={2} value={form.remarks} onChange={(e) => set({ remarks: e.target.value })} maxLength={500} />
          </div>

          {error && <p className="apt-error-text" role="alert">{error}</p>}
        </div>

        <div className="apt-modal-foot">
          <button type="button" className="apt-btn" onClick={onClose} disabled={submitting}>Close</button>
          <button type="submit" className="apt-btn apt-btn-primary" disabled={submitting}>{submitting ? 'Booking…' : (form.repeat ? 'Book appointments' : 'Book appointment')}</button>
        </div>
      </form>
    </div>
  );
};

export default AppointmentBookingModal;
